import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  Fab,
  Grid,
} from '@mui/material';
import { Upload } from '@mui/icons-material';
import { SnackbarProvider, enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import api from '../../api/api';
import CreateEditEmployeeDialog from './CreateEditEmployeeDialog';
import * as XLSX from 'xlsx';

const PAGE_SIZE = 5;
const EmployeeListingPage = () => {

  var utc = require("dayjs/plugin/utc");
  dayjs.extend(utc);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogType, setDialogType] = useState('add'); // 'add' or 'edit'

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/api/Employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/api/Departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments', error);
    }
  };

  const handleOpenDialog = (type, employee = null) => {
    setDialogType(type);
    setCurrentEmployee(employee);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentEmployee(null);
  };

  const handleSaveEmployee = () => {
    fetchEmployees();
  };

  const handleDeleteEmployee = async () => {
    try {
      await api.delete(`/api/Employees/${currentEmployee.id}`);
      fetchEmployees();
      handleCloseDialog();
    } catch (error) {
      console.error('Error deleting employee', error);
    }
  };

  const handleMenuOpen = (event, employee) => {
    setCurrentEmployee(employee);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const triggerFileInput = () => {
    document.getElementById('file-upload').click();
  };

  const handleImportEmployees = async (event) => {

    const file = event.target.files[0];
    if (!file) return;

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(worksheet);

    // Map the data from Excel to API format
    const mappedEmployees = [];
    console.log([...new Set(jsonData.map((v) => { return v.Department }))]);
    for (const row of jsonData) {
      console.log(row);
      const department = departments.find(d => d.name === row.Department);
      if (department == null) {
        enqueueSnackbar(`Failed to import employees, ${row.Department} department not found!`, { variant: 'error' });
        return;
      }
      mappedEmployees.push({
        employeeCode: row['Emp Code'],
        name: row['Full Name'],
        joiningDate: dayjs(row['Joining Date']).format('YYYY-MM-DD'),
        departmentId: department ? department.id : null,
        designation: row.Designation,
      });
    }

    try {
      await api.post('/api/Employees', mappedEmployees);
      fetchEmployees();
      enqueueSnackbar('Employees imported successfully!', { variant: 'success', autoHideDuration: 3000, });
    } catch (error) {
      console.error('Error importing employees', error);
      enqueueSnackbar('Failed to import employees', { variant: 'error', autoHideDuration: 3000, });
    }
  };

  return (
    <Box sx={{ p: 2 }}>

      <Typography marginBottom={2} component="p" variant="h5">Employees</Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : employees.length === 0 ? (
        <Typography>No Employee Added.</Typography>
      ) : (
        <Grid container spacing={2}>
          {employees.map((employee) => (
            <Grid item xs={12} sm={6} md={4} key={employee.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6">{employee.name}</Typography>
                      <Typography variant="body2">Employee Code: {employee.employeeCode}</Typography>
                      <Typography variant="body2">Joining Date: {dayjs(employee.joiningDate).utc('z').local().format("DD/MM/YYYY")}</Typography>
                      <Typography variant="body2">Department: {employee.department.name}</Typography>
                      <Typography variant="body2">Designation: {employee.designation}</Typography>
                    </Box>
                    <IconButton
                      aria-label="more"
                      aria-controls="long-menu"
                      aria-haspopup="true"
                      onClick={(event) => handleMenuOpen(event, employee)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <SnackbarProvider />
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleOpenDialog('edit', currentEmployee);
          handleMenuClose();
        }}>Edit</MenuItem>
        <MenuItem onClick={() => {
          handleOpenDialog('delete', currentEmployee);
          handleMenuClose();
        }}>Delete</MenuItem>
      </Menu>
      <Fab color="primary" aria-label="add" sx={{ position: 'fixed', bottom: 16, right: 16 }} onClick={() => handleOpenDialog('add')}>
        <AddIcon />
      </Fab>
      <Fab color="success" aria-label="upload" style={{ position: 'fixed', bottom: 16, right: 86 }} onClick={triggerFileInput}>
        <Upload />
      </Fab>
      <input
        accept=".xlsx, .xls"
        style={{ display: 'none' }}
        id="file-upload"
        type="file"
        onChange={handleImportEmployees}
      />


      {(dialogType === 'add' || dialogType === 'edit') && currentEmployee && (
        <CreateEditEmployeeDialog open={openDialog} handleClose={handleCloseDialog} employee={currentEmployee} onSave={handleSaveEmployee} />
      )}
      {dialogType === 'delete' && (
        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this employee?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleDeleteEmployee} variant="contained" color="secondary">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default EmployeeListingPage;
